import {
  ConnectorType,
  GA4FilterItem,
  Metric,
  MetricsByConnectorType,
  Split,
} from 'src/utils/gql/types';

import {
  AppliedFilter,
  AppliedFilterGroup,
  FilterWithValue,
  MetricFilterOperatorKey,
  TableRowsType,
} from './types';

export function sortArrayBySecondArray<T extends { id: string }>(
  firstArray: T[],
  secondArray: T[],
): T[] {
  const secondArrayIds = new Set(secondArray.map((item) => item.id));

  return firstArray.sort((a, b) => {
    const aInSecond = secondArrayIds.has(a.id) ? 0 : 1;
    const bInSecond = secondArrayIds.has(b.id) ? 0 : 1;
    return aInSecond - bInSecond;
  });
}

function sortByMainArray<A extends { id: string }, T extends { id: string }>(
  mainArray: T[],
  rows: A[],
): A[] {
  const indexMap = new Map(mainArray.map((item, index) => [item.id, index]));

  return [...rows].sort((a, b) => indexMap.get(a.id)! - indexMap.get(b.id)!);
}

export const sortBySplitsAndMetrics = (
  rows: TableRowsType[],
  ids?: string[],
) => {
  if (ids?.length) {
    return sortByMainArray(
      ids.map((id) => ({ id })),
      rows,
    ).sort((a, b) => {
      if (a.isSplit && !b.isSplit) {
        return -1;
      }
      if (!a.isSplit && b.isSplit) {
        return 1;
      }
      return 0;
    });
  }

  return [...rows].sort((a, b) => {
    if (a.isSplit && !b.isSplit) {
      return -1;
    }
    if (!a.isSplit && b.isSplit) {
      return 1;
    }
    return 0;
  });
};

export const getTitle = (str: string) => {
  return str
    .replace(/[_\.]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();
};

export const buildColumnId = (
  key: string,
  type: ConnectorType | MetricsByConnectorType,
) => `${type}::${key}`;

const SHOPIFY_NON_ADDITIVE_METRIC_IDS = new Set([
  'average_order_value',
  'average_units_ordered',
  'life_time_value',
  'discount_rate',
]);

/** Chiavi GAQL / logiche equivalenti per join e commonSplits (Google Ads). */
const JOIN_SPLIT_CANONICAL: Record<string, string> = {
  search_term: 'search_term',
  'search_term_view.search_term': 'search_term',
  'campaign_search_term_view.search_term': 'search_term',
  keyword: 'keyword',
  'ad_group_criterion.keyword.text': 'keyword',
  'segments.keyword.info.text': 'keyword',
  campaign: 'campaign',
  'campaign.name': 'campaign',
  ad_group: 'ad_group',
  'ad_group.name': 'ad_group',
  device: 'device',
  'segments.device': 'device',
  ad_network: 'ad_network',
  ad_network_type: 'ad_network',
  'segments.ad_network_type': 'ad_network',
  ad_using_product_data: 'ad_using_product_data',
  'segments.ad_using_product_data': 'ad_using_product_data',
  search_term_match_source: 'search_term_match_source',
  'segments.search_term_match_source': 'search_term_match_source',
  match_source: 'search_term_match_source',
};

/** Normalizza la chiave split per chiavi di join e matching cross-connettore. */
export const joinSplitNormalizedKey = (key: string): string =>
  JOIN_SPLIT_CANONICAL[key] ?? key;

/** True se due chiavi split (anche GAQL vs logica) rappresentano la stessa dimensione. */
export const splitKeysEquivalent = (a: string, b: string): boolean =>
  joinSplitNormalizedKey(a) === joinSplitNormalizedKey(b);

/** Allinea header (catalogo / form) e celle costruite dalla risposta API. */
export const cellsMatchColumnId = (
  headerColumnId: string,
  cellColumnId: string,
): boolean => {
  if (headerColumnId === cellColumnId) return true;
  const ga = ConnectorType.GOOGLE_ADS;
  const gaPrefix = `${ga}::`;
  // ROAS: catalogo / API possono usare `roas` o `metrics.roas`
  const roasA = buildColumnId('metrics.roas', ga);
  const roasB = buildColumnId('roas', ga);
  if (
    (headerColumnId === roasA && cellColumnId === roasB) ||
    (headerColumnId === roasB && cellColumnId === roasA)
  ) {
    return true;
  }
  // Google Ads: stesso split con chiave GAQL vs logica (search_term, keyword, …)
  if (
    headerColumnId.startsWith(gaPrefix) &&
    cellColumnId.startsWith(gaPrefix)
  ) {
    const hk = headerColumnId.slice(gaPrefix.length);
    const ck = cellColumnId.slice(gaPrefix.length);
    if (joinSplitNormalizedKey(hk) === joinSplitNormalizedKey(ck)) {
      return true;
    }
  }
  return false;
};

/** Catalogo split vs chiavi nelle righe salvate (es. search_term in row, search_term_view.search_term in catalogo). */
export const googleAdsSplitCatalogMatchesRowKey = (
  catalogSplitKey: string,
  rowSplitKey: string,
): boolean =>
  joinSplitNormalizedKey(catalogSplitKey) ===
  joinSplitNormalizedKey(rowSplitKey);

/** Catalogo metrica vs chiave metrica nelle righe salvate (es. roas vs metrics.roas). */
export const googleAdsMetricCatalogMatchesRowKey = (
  catalogMetricKey: string,
  rowMetricKey: string,
): boolean => {
  if (catalogMetricKey === rowMetricKey) return true;
  const ga = ConnectorType.GOOGLE_ADS;
  return cellsMatchColumnId(
    buildColumnId(catalogMetricKey, ga),
    buildColumnId(rowMetricKey, ga),
  );
};

export const getFiltersByType: (
  filters: FilterWithValue[],
  type: ConnectorType,
) => GA4FilterItem[] = (filters: FilterWithValue[], type: ConnectorType) => {
  return filters
    .filter(
      (filter) =>
        type === filter.connectorType || filter.connectorType === 'kipify',
    )
    .map(({ values, key }) => {
      if (
        Array.isArray(values) &&
        values.length > 0 &&
        typeof values[0] === 'object' &&
        values[0] !== null &&
        'filtersGroup' in values[0]
      ) {
        return {
          key,
          filterGroupOr: values.map((val: any) => ({
            rulesAnd: val.filtersGroup,
          })),
        };
      } else {
        return {
          key,
          filterGroupOr: values.map((val: any) => ({
            rulesAnd: [val],
          })),
        };
      }
    })
    .filter((filter) => {
      if (filter.filterGroupOr.length === 0) return false;
      return !filter.filterGroupOr.some((groupOrItem) => {
        return groupOrItem.rulesAnd.some(
          (ruleAndItem: any) =>
            !ruleAndItem ||
            !ruleAndItem.values ||
            ruleAndItem.values.length === 0,
        );
      });
    });
};

export const getMetricsKeys = (
  metrics: Metric[],
  type: ConnectorType | MetricsByConnectorType,
) => metrics.filter((metric) => type === metric.type).map(({ key }) => key);

export const getSplitsKeys = (splits: Split[], type: ConnectorType) =>
  splits
    .filter(
      (split) =>
        type === split.connectorType || split.connectorType === 'kipify',
    )
    .map(({ key }) => key);

export const getMetricsLabels = (
  metrics: Metric[],
  type: ConnectorType | MetricsByConnectorType,
) => metrics.filter((metric) => type === metric.type).map(({ label }) => label);

export const getSplitsLabels = (splits: Split[], type: ConnectorType) =>
  splits
    .filter(
      (split) =>
        type === split.connectorType || split.connectorType === 'kipify',
    )
    .map(({ label }) => label);

const checkValueMatchFilter = (
  value: number,
  appliedFilter: AppliedFilter,
): boolean => {
  if (!appliedFilter.values?.[0]?.length) {
    return true;
  }

  const comparingValue = Number(appliedFilter.values[0]);

  switch (appliedFilter.operator) {
    case MetricFilterOperatorKey.Is:
      return value === comparingValue;
    case MetricFilterOperatorKey.IsNot:
      return value !== comparingValue;
    case MetricFilterOperatorKey.GreaterThan:
      return value > comparingValue;
    case MetricFilterOperatorKey.LowerThan:
      return value < comparingValue;
  }

  return true;
};

export const checkValueMatchFilters = (
  value: number,
  appliedFilters: Array<AppliedFilterGroup>,
): boolean =>
  appliedFilters.some((group) =>
    'filtersGroup' in group
      ? group.filtersGroup.every((filter) =>
          checkValueMatchFilter(value, filter),
        )
      : checkValueMatchFilter(value, group),
  );

const COUNTRY_SPLIT_IDS = new Set([
  'target_country',
  'user_country',
  'country',
  'shipping_country',
  'billing_country',
]);

const REGION_SPLIT_IDS = new Set(['target_region', 'user_region']);

/**
 * Estrae il codice paese ISO2 da una stringa di regione nel formato "Region Name (XX)"
 * Esempio: "Lombardia (IT)" -> "IT"
 */
export const extractCountryCodeFromRegion = (
  regionValue: string | null | undefined,
): string | null => {
  if (!regionValue || typeof regionValue !== 'string') return null;

  // Cerca pattern "(XX)" dove XX è un codice paese ISO2
  const match = regionValue.match(/\(([A-Z]{2})\)/);
  if (match && match[1]) {
    return match[1];
  }

  return null;
};

const COUNTRY_PRIORITY_ORDER = [
  'target_country',
  'user_country',
  'country',
  'shipping_country',
  'billing_country',
];

// Manteniamo l'elenco dei campi billing da sostituire con shipping per evitare duplicati, lasciando fuori billing_country così da mostrarlo.
const BILLING_TO_SHIPPING_PRIORITY_MAP: Record<string, string> = {
  billing_city: 'shipping_city',
  billing_province: 'shipping_province',
  billing_province_code: 'shipping_province_code',
  billing_country_code: 'shipping_country_code',
  billing_zip: 'shipping_zip',
};

const COUNTRY_CODE_MAP: Record<string, string> = {
  IT: 'Italy',
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  NL: 'Netherlands',
  BE: 'Belgium',
  CH: 'Switzerland',
  AT: 'Austria',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  CZ: 'Czech Republic',
  HU: 'Hungary',
  RO: 'Romania',
  BG: 'Bulgaria',
  HR: 'Croatia',
  SI: 'Slovenia',
  SK: 'Slovakia',
  LT: 'Lithuania',
  LV: 'Latvia',
  EE: 'Estonia',
  IE: 'Ireland',
  PT: 'Portugal',
  GR: 'Greece',
  CY: 'Cyprus',
  MT: 'Malta',
  LU: 'Luxembourg',
  CA: 'Canada',
  AU: 'Australia',
  NZ: 'New Zealand',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  IN: 'India',
  BR: 'Brazil',
  MX: 'Mexico',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  VE: 'Venezuela',
  UY: 'Uruguay',
  PY: 'Paraguay',
  BO: 'Bolivia',
  EC: 'Ecuador',
  GY: 'Guyana',
  SR: 'Suriname',
  FK: 'Falkland Islands',
  GF: 'French Guiana',
  ZA: 'South Africa',
  EG: 'Egypt',
  NG: 'Nigeria',
  KE: 'Kenya',
  GH: 'Ghana',
  ET: 'Ethiopia',
  TZ: 'Tanzania',
  UG: 'Uganda',
  DZ: 'Algeria',
  MA: 'Morocco',
  TN: 'Tunisia',
  LY: 'Libya',
  SD: 'Sudan',
  SS: 'South Sudan',
  CF: 'Central African Republic',
  TD: 'Chad',
  NE: 'Niger',
  ML: 'Mali',
  BF: 'Burkina Faso',
  CI: 'Ivory Coast',
  SN: 'Senegal',
  GN: 'Guinea',
  SL: 'Sierra Leone',
  LR: 'Liberia',
  TG: 'Togo',
  BJ: 'Benin',
  GW: 'Guinea-Bissau',
  CV: 'Cape Verde',
  GM: 'Gambia',
  MR: 'Mauritania',
  DJ: 'Djibouti',
  SO: 'Somalia',
  ER: 'Eritrea',
  RW: 'Rwanda',
  BI: 'Burundi',
  MG: 'Madagascar',
  MU: 'Mauritius',
  SC: 'Seychelles',
  KM: 'Comoros',
  YT: 'Mayotte',
  RE: 'Réunion',
  ZW: 'Zimbabwe',
};

export const normalizeCountryCode = (countryCode: string): string => {
  const normalizedCode = countryCode?.toUpperCase?.();

  if (!normalizedCode) {
    return countryCode;
  }

  return COUNTRY_CODE_MAP[normalizedCode] ?? countryCode;
};

const findCountryItem = (row: TableRowsType[]): TableRowsType | undefined => {
  for (const key of COUNTRY_PRIORITY_ORDER) {
    const countryItem = row.find((item) => item.isSplit && item.id === key);
    if (countryItem) {
      return countryItem;
    }
  }

  return undefined;
};

/** Normalizza product_type per merge Markup Kipify ↔ Gross Sales Shopify. */
export const normalizeProductTypeForGroupKey = (raw: string): string =>
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s{2,}/g, ' ');

/**
 * Riga Kipify: solo split product_type + metrica markup (output backend per Custom Report).
 */
const isKipifyMarkupPerProductTypeRow = (row: TableRowsType[]): boolean => {
  const splits = row.filter((c) => c.isSplit);
  const metrics = row.filter((c) => !c.isSplit);
  if (splits.length !== 1 || metrics.length < 1) return false;
  const s = splits[0];
  if (s.id !== 'product_type' || s.type !== ConnectorType.KIPIFY) return false;
  return metrics.some(
    (m) =>
      m.type === ConnectorType.KIPIFY &&
      (m.id === 'markup' || m.id === 'kipify_markup'),
  );
};

/**
 * Unisce righe Markup Kipify (per product_type) sulle righe Shopify/Ads con lo stesso tipo.
 * Necessario perché product_type è split “granulare” e altrimenti groupRowsByCountry non aggrega.
 */
export const mergeKipifyMarkupIntoProductTypeRows = (
  rows: TableRowsType[][],
): TableRowsType[][] => {
  const byPt = new Map<string, TableRowsType[][]>();
  const rest: TableRowsType[][] = [];

  for (const row of rows) {
    if (isKipifyMarkupPerProductTypeRow(row)) {
      const pt = row.find((c) => c.isSplit && c.id === 'product_type')!;
      const k = normalizeProductTypeForGroupKey(String(pt.value ?? ''));
      const arr = byPt.get(k) ?? [];
      arr.push(row);
      byPt.set(k, arr);
    } else {
      rest.push(row);
    }
  }

  if (byPt.size === 0) return rows;

  const consumed = new Set<string>();
  const out: TableRowsType[][] = [];

  for (const row of rest) {
    const hostPt = row.find(
      (c) =>
        c.isSplit && c.id === 'product_type' && c.type !== ConnectorType.KIPIFY,
    );
    if (hostPt) {
      const k = normalizeProductTypeForGroupKey(String(hostPt.value ?? ''));
      const kipifyRows = byPt.get(k);
      if (kipifyRows?.length) {
        const cellMap = new Map<string, TableRowsType>(
          row.map((c) => [c.columnId, c]),
        );
        for (const kr of kipifyRows) {
          for (const c of kr) {
            if (
              c.isSplit &&
              c.type === ConnectorType.KIPIFY &&
              c.id === 'product_type'
            ) {
              continue;
            }
            if (!cellMap.has(c.columnId)) cellMap.set(c.columnId, c);
          }
        }
        out.push(Array.from(cellMap.values()));
        consumed.add(k);
        continue;
      }
    }
    out.push(row);
  }

  for (const [k, krs] of Array.from(byPt.entries())) {
    if (consumed.has(k)) continue;
    for (const kr of krs) out.push(kr);
  }

  return out;
};

const addRowToGroup = (
  groupedRow: TableRowsType[],
  row: TableRowsType[],
  isShopifyRow: boolean,
) => {
  // Complessità: O(n) dove n è il numero di celle nella riga.
  row.forEach((item) => {
    if (isShopifyRow && item.isSplit) {
      const correspondingShippingKey =
        BILLING_TO_SHIPPING_PRIORITY_MAP[item.id];

      if (
        correspondingShippingKey &&
        row.some(
          (candidate) =>
            candidate.isSplit &&
            candidate.id === correspondingShippingKey &&
            candidate.type === item.type,
        )
      ) {
        return;
      }
    }

    const existingIndex = groupedRow.findIndex(
      (existing) => existing.id === item.id && existing.type === item.type,
    );

    if (existingIndex === -1) {
      groupedRow.push(item);
      return;
    }

    const existing = groupedRow[existingIndex];

    if (
      !item.isSplit &&
      item.type === ConnectorType.SHOPIFY &&
      SHOPIFY_NON_ADDITIVE_METRIC_IDS.has(item.id)
    ) {
      return;
    }

    if (typeof item.value === 'number' && typeof existing.value === 'number') {
      groupedRow[existingIndex] = {
        ...existing,
        value: existing.value + item.value,
      };
    }
  });
};

const shouldGroupByCountry = (rows: TableRowsType[][]): boolean =>
  rows.some((row) =>
    row.some((item) => item.isSplit && COUNTRY_SPLIT_IDS.has(item.id)),
  );

/**
 * Determina se uno split è "granulare" (non aggregabile).
 * Gli split granulari rappresentano entità distinte che non dovrebbero essere aggregate insieme.
 * Complessità: O(1) - pattern matching semplice.
 *
 * @param splitId - L'ID dello split da controllare
 * @returns true se lo split è granulare e non dovrebbe essere aggregato
 */
const isGranularSplit = (splitId: string): boolean => {
  const normalizedId = splitId.toLowerCase();

  // Identificatori unici (id, code)
  if (normalizedId.includes('_id') || normalizedId.includes('_code')) {
    // Eccezioni: gli split di paese/regione con _code sono aggregabili
    if (
      normalizedId.includes('country_code') ||
      normalizedId.includes('province_code') ||
      normalizedId.includes('region_code')
    ) {
      return false;
    }
    return true;
  }

  // Entità specifiche (product, customer, order, variant, collection)
  if (
    normalizedId.startsWith('product_') ||
    normalizedId === 'product_title' ||
    normalizedId.startsWith('customer_') ||
    normalizedId.startsWith('order_') ||
    normalizedId.startsWith('variant_') ||
    normalizedId.startsWith('collection_')
  ) {
    // Eccezioni: alcuni attributi aggregabili
    if (
      normalizedId === 'order_creation_date' ||
      normalizedId === 'customer_total_spent' ||
      normalizedId === 'customer_first_order_date' ||
      normalizedId === 'customer_first_order_month'
    ) {
      return false;
    }
    return true;
  }

  // Tag specifici
  if (normalizedId.includes('_tag') || normalizedId.endsWith('_tag')) {
    return true;
  }

  // Altri split granulari noti
  if (
    normalizedId === 'customer_type' ||
    normalizedId === 'customertype' ||
    normalizedId === 'customer type'
  ) {
    return true;
  }

  // Vendor è uno split granulare: ogni vendor deve avere la sua riga separata
  if (normalizedId === 'vendor' || normalizedId.includes('vendor')) {
    return true;
  }

  return false;
};

interface GroupRowsByCountryOptions {
  hasKipifyCountry: boolean;
}

/**
 * Raggruppa le righe per paese rispettando la priorità shipping>billing per Shopify.
 * Complessità: O(m * n) dove m è il numero di righe e n le celle per riga.
 * Esempio d'uso:
 * groupRowsByCountry(rows, { hasKipifyCountry: false });
 */
export const groupRowsByCountry = (
  rows: TableRowsType[][],
  { hasKipifyCountry }: GroupRowsByCountryOptions,
): TableRowsType[][] => {
  if (!rows.length || hasKipifyCountry) {
    return rows;
  }

  // Se non ci sono split di paese, controlla se ci sono altri splits che richiedono raggruppamento
  if (!shouldGroupByCountry(rows)) {
    if (rows.length === 0) return rows;

    // Fix generale: se ci sono split granulari (entità distinte come product_id, customer_id, order_discount_code, etc.),
    // NON aggregare le righe, altrimenti si perde il dettaglio.
    // Gli split granulari sono identificati dinamicamente dalla funzione isGranularSplit.
    // In questi casi vogliamo mantenere una riga per ogni valore dello split, anche senza country.
    const hasGranularSplits = rows.some((row) =>
      row.some(
        (item) =>
          item.isSplit &&
          item.type === ConnectorType.SHOPIFY &&
          isGranularSplit(item.id),
      ),
    );

    if (hasGranularSplits) {
      return mergeKipifyMarkupIntoProductTypeRows(rows);
    }

    // Separa le righe per tipo di connettore
    const shopifyRows: TableRowsType[][] = [];
    const otherRows: TableRowsType[][] = [];

    rows.forEach((row) => {
      const isShopifyRow = row.some(
        (item) => item.type === ConnectorType.SHOPIFY,
      );
      if (isShopifyRow) {
        shopifyRows.push(row);
      } else {
        otherRows.push(row);
      }
    });

    // Per GA4/Ads/Facebook/Database: mantieni sempre le righe separate quando non c'è paese
    // (non aggregare come Shopify, perché hanno splits come eventName, campaign, etc. che devono rimanere distinti)
    const processedOtherRows = otherRows;

    // Per Shopify: aggrega sempre quando non c'è paese (comportamento originale)
    const aggregatedShopifyRow: TableRowsType[] = [];
    const seenCells = new Map<string, TableRowsType>();

    shopifyRows.forEach((row) => {
      row.forEach((item) => {
        const cellKey = `${item.type}_${item.id}`;

        if (item.isSplit) {
          const correspondingShippingKey =
            BILLING_TO_SHIPPING_PRIORITY_MAP[item.id];

          if (
            correspondingShippingKey &&
            row.some(
              (candidate) =>
                candidate.isSplit &&
                candidate.id === correspondingShippingKey &&
                candidate.type === item.type,
            )
          ) {
            return;
          }
        }

        if (!seenCells.has(cellKey)) {
          seenCells.set(cellKey, item);
          aggregatedShopifyRow.push(item);
        } else {
          const existing = seenCells.get(cellKey)!;
          if (
            typeof item.value === 'number' &&
            typeof existing.value === 'number'
          ) {
            existing.value = existing.value + item.value;
          }
        }
      });
    });

    // Combina righe Shopify aggregate con righe altri connettori (separate o aggregate)
    const result: TableRowsType[][] = [];
    if (aggregatedShopifyRow.length > 0) {
      result.push(aggregatedShopifyRow);
    }
    // Usa concat invece di spread operator per evitare stack overflow con array grandi
    for (const row of processedOtherRows) {
      result.push(row);
    }

    return result;
  }

  // Raggruppa per combinazione di TUTTI gli split, non solo per paese
  // Questo evita di aggregare righe che hanno split diversi (es. product_title diverso)
  const groupedByAllSplits = new Map<string, TableRowsType[]>();

  rows.forEach((row, rowIndex) => {
    // Crea una chiave univoca basata su tutti gli split della riga
    const splitKeyParts: string[] = [];

    // Ordina gli split per avere una chiave consistente
    const sortedSplits = row
      .filter((item) => item.isSplit)
      .sort((a, b) => {
        // Ordina per tipo connettore, poi per ID
        if (a.type !== b.type) {
          return a.type.localeCompare(b.type);
        }
        return a.id.localeCompare(b.id);
      });

    sortedSplits.forEach((splitItem) => {
      // Per split di paese, normalizza il valore
      if (COUNTRY_SPLIT_IDS.has(splitItem.id)) {
        const countryValue = splitItem.value;
        const normalizedCountry =
          typeof countryValue === 'string'
            ? normalizeCountryCode(countryValue)
            : String(countryValue);
        splitKeyParts.push(
          `${splitItem.type}::${splitItem.id}::${normalizedCountry}`,
        );
      } else {
        // Per altri split, usa il valore così com'è
        const value = splitItem.value ?? '';
        splitKeyParts.push(
          `${splitItem.type}::${splitItem.id}::${String(value)}`,
        );
      }
    });

    // Se non ci sono split, usa un indice univoco per mantenere la riga separata
    const groupKey =
      splitKeyParts.length > 0
        ? splitKeyParts.join('||')
        : `__no_splits_${rowIndex}`;

    if (!groupedByAllSplits.has(groupKey)) {
      groupedByAllSplits.set(groupKey, []);
    }

    const groupedRow = groupedByAllSplits.get(groupKey)!;
    const isShopifyRow = row.some(
      (item) => item.type === ConnectorType.SHOPIFY,
    );

    // Aggrega solo le metriche, mantenendo gli split distinti
    addRowToGroup(groupedRow, row, isShopifyRow);
  });

  return Array.from(groupedByAllSplits.values());
};

/**
 * Aggrega le righe in base a kipify_country mantenendo le priorità Shopify.
 * Complessità: O(m * n) con m righe e n celle.
 * Esempio d'uso:
 * groupRowsByKipifyCountry(rows);
 */
export const groupRowsByKipifyCountry = (
  rows: TableRowsType[][],
): TableRowsType[][] => {
  if (!rows.length) {
    return rows;
  }

  const groupedByKipifyCountry = new Map<string, TableRowsType[]>();

  rows.forEach((row, rowIndex) => {
    const kipifyCountryItem = row.find(
      (item) => item.isSplit && item.id === 'kipify_country',
    );

    if (!kipifyCountryItem) {
      // Se non c'è kipify_country, unisci tutte le righe senza split in una singola riga
      const noSplitKey = '__no_split__';
      if (!groupedByKipifyCountry.has(noSplitKey)) {
        groupedByKipifyCountry.set(noSplitKey, []);
      }
      const groupedRow = groupedByKipifyCountry.get(noSplitKey)!;
      const isShopifyRow = row.some(
        (item) => item.type === ConnectorType.SHOPIFY,
      );
      addRowToGroup(groupedRow, row, isShopifyRow);
      return;
    }

    const countryValue = kipifyCountryItem.value;
    const normalizedCountry = String(countryValue);

    if (!groupedByKipifyCountry.has(normalizedCountry)) {
      groupedByKipifyCountry.set(normalizedCountry, []);
    }

    const groupedRow = groupedByKipifyCountry.get(normalizedCountry)!;
    const isShopifyRow = row.some(
      (item) => item.type === ConnectorType.SHOPIFY,
    );

    addRowToGroup(groupedRow, row, isShopifyRow);
  });

  return Array.from(groupedByKipifyCountry.values());
};
